/**
 * PDF Page Manipulation Utilities
 *
 * Functions for inserting, deleting, and reordering PDF pages using pdf-lib
 */

import * as PDFLib from 'pdf-lib';

export interface PageOperationResult {
  pdfBytes: Uint8Array;
  pageNumberMap: Map<number, number>; // oldPageNum -> newPageNum
}

/**
 * Insert pages from another PDF after a specified page
 */
export async function insertPagesFromPDF(
  currentPdfBytes: Uint8Array,
  insertPdfBytes: Uint8Array,
  afterPageNumber: number
): Promise<PageOperationResult> {
  const currentPdf = await PDFLib.PDFDocument.load(currentPdfBytes);
  const insertPdf = await PDFLib.PDFDocument.load(insertPdfBytes);

  const totalInsertPages = insertPdf.getPageCount();
  const insertIndices = Array.from({ length: totalInsertPages }, (_, i) => i);

  // Copy all pages from the insert PDF
  const copiedPages = await currentPdf.copyPages(insertPdf, insertIndices);

  // Insert pages after the specified page number (convert to 0-based index)
  const insertIndex = afterPageNumber; // Insert after means insert at this index
  copiedPages.forEach((page, i) => {
    currentPdf.insertPage(insertIndex + i, page);
  });

  // Build page number mapping
  // Pages 1 to afterPageNumber stay the same
  // New pages inserted
  // Pages after shift up by totalInsertPages
  const pageNumberMap = new Map<number, number>();
  const totalPages = currentPdf.getPageCount();
  const originalPageCount = totalPages - totalInsertPages;

  // Map all ORIGINAL pages to their new positions
  for (let oldPage = 1; oldPage <= originalPageCount; oldPage++) {
    if (oldPage <= afterPageNumber) {
      pageNumberMap.set(oldPage, oldPage);
    } else {
      pageNumberMap.set(oldPage, oldPage + totalInsertPages);
    }
  }

  const pdfBytes = await currentPdf.save();
  return { pdfBytes, pageNumberMap };
}

/**
 * Delete a specific page from the PDF
 */
export async function deletePage(
  pdfBytes: Uint8Array,
  pageNumber: number
): Promise<PageOperationResult> {
  const pdfDoc = await PDFLib.PDFDocument.load(pdfBytes);

  // Remove page (convert to 0-based index)
  pdfDoc.removePage(pageNumber - 1);

  // Build page number mapping
  // Pages before the deleted page stay the same
  // Pages after the deleted page shift down by 1
  const pageNumberMap = new Map<number, number>();
  const originalTotalPages = pdfDoc.getPageCount() + 1; // Add back the deleted page for original count

  for (let oldPage = 1; oldPage < originalTotalPages; oldPage++) {
    if (oldPage < pageNumber) {
      pageNumberMap.set(oldPage, oldPage);
    } else if (oldPage === pageNumber) {
      // Deleted page maps to null (will be filtered out)
      pageNumberMap.set(oldPage, -1);
    } else {
      pageNumberMap.set(oldPage, oldPage - 1);
    }
  }

  const newPdfBytes = await pdfDoc.save();
  return { pdfBytes: newPdfBytes, pageNumberMap };
}

/**
 * Reorder pages by moving a page from one position to another
 */
export async function reorderPages(
  pdfBytes: Uint8Array,
  fromPageNumber: number,
  toPageNumber: number
): Promise<PageOperationResult> {
  const pdfDoc = await PDFLib.PDFDocument.load(pdfBytes);

  const fromIndex = fromPageNumber - 1;
  const toIndex = toPageNumber - 1;

  // Get all pages
  const pages = pdfDoc.getPages();
  const pageToMove = pages[fromIndex];

  // Remove the page from its current position
  pdfDoc.removePage(fromIndex);

  // Insert it at the new position
  // After removing the page, we insert at toIndex
  // pdf-lib handles the shifted indices correctly - we want the page at position toPageNumber,
  // which is index toIndex (0-based)
  pdfDoc.insertPage(toIndex, pageToMove);

  // Build page number mapping
  const pageNumberMap = new Map<number, number>();
  const totalPages = pdfDoc.getPageCount();

  if (fromPageNumber < toPageNumber) {
    // Moving down: fromPage goes to toPage, pages between shift up
    for (let oldPage = 1; oldPage <= totalPages; oldPage++) {
      if (oldPage < fromPageNumber) {
        pageNumberMap.set(oldPage, oldPage);
      } else if (oldPage === fromPageNumber) {
        pageNumberMap.set(oldPage, toPageNumber);
      } else if (oldPage <= toPageNumber) {
        pageNumberMap.set(oldPage, oldPage - 1);
      } else {
        pageNumberMap.set(oldPage, oldPage);
      }
    }
  } else {
    // Moving up: fromPage goes to toPage, pages between shift down
    for (let oldPage = 1; oldPage <= totalPages; oldPage++) {
      if (oldPage < toPageNumber) {
        pageNumberMap.set(oldPage, oldPage);
      } else if (oldPage < fromPageNumber) {
        pageNumberMap.set(oldPage, oldPage + 1);
      } else if (oldPage === fromPageNumber) {
        pageNumberMap.set(oldPage, toPageNumber);
      } else {
        pageNumberMap.set(oldPage, oldPage);
      }
    }
  }

  const newPdfBytes = await pdfDoc.save();
  return { pdfBytes: newPdfBytes, pageNumberMap };
}

/**
 * Update annotations/fields/comments pageNum based on page number mapping
 */
export function updateAnnotationPageNumbers<T extends { pageNum: number }>(
  items: T[],
  pageNumberMap: Map<number, number>
): T[] {
  return items
    .map(item => {
      const newPageNum = pageNumberMap.get(item.pageNum);
      if (newPageNum === undefined || newPageNum === -1) {
        // Page was deleted, filter out this item
        return null;
      }
      return {
        ...item,
        pageNum: newPageNum,
      };
    })
    .filter((item): item is T => item !== null);
}
